import { useEffect, useState } from "react";
import { fetchRequests, decideRequest, type TimeOffRequest } from "../lib/api";
import RequestTable from "./RequestTable";
import DenyDialog from "./DenyDialog";

/**
 * Executive-only queue of pending requests whose primary approver is
 * currently away (see isAway/isBackupApproverOf on the server). Deliberately
 * separate from the Acknowledgements tab and from Org-wide reporting: this
 * list should normally be empty, and only ever shows an actual gap that
 * needs covering -- not a parallel inbox for every pending request
 * company-wide.
 *
 * Lives on the Admin tab alongside ExecutiveAccessPanel, as a section rather
 * than its own page.
 */
export default function BackupApprovalsPanel() {
  const [requests, setRequests] = useState<TimeOffRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [denying, setDenying] = useState<TimeOffRequest | null>(null);

  function reload() {
    fetchRequests("backup")
      .then(setRequests)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load backup acknowledgements"));
  }

  useEffect(reload, []);

  async function handleApprove(id: string) {
    setBusyId(id);
    try {
      await decideRequest(id, "approve");
      setRequests((prev) => prev?.filter((r) => r.id !== id) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to acknowledge request");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDenyConfirm(reason: string) {
    if (!denying) return;
    setBusyId(denying.id);
    try {
      await decideRequest(denying.id, "deny", reason);
      setRequests((prev) => prev?.filter((r) => r.id !== denying.id) ?? null);
      setDenying(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send this back for coordination");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="di-card">
      <h2 className="di-h4">Backup acknowledgements</h2>
      <p className="di-small">
        Pending requests where the primary approver is currently away. As an executive, you can
        step in and act on them here -- this list is normally empty, and only shows something when
        there's an actual gap to cover.
      </p>

      {error && <p className="form-error">{error}</p>}

      {requests === null ? (
        <p className="di-small">Loading…</p>
      ) : (
        <RequestTable
          requests={requests}
          showPerson
          emptyMessage="Nothing needs backup coverage right now."
          renderActions={(r) => (
            <div className="action-group">
              <button
                className="di-btn di-btn-primary di-btn-small"
                disabled={busyId === r.id}
                onClick={() => handleApprove(r.id)}
              >
                Acknowledge
              </button>
              <button
                className="di-btn di-btn-outline di-btn-small"
                disabled={busyId === r.id}
                onClick={() => setDenying(r)}
              >
                Needs Coordination
              </button>
            </div>
          )}
        />
      )}

      {denying && (
        <DenyDialog
          requesterName={denying.person_name ?? denying.person_email}
          submitting={busyId === denying.id}
          onCancel={() => setDenying(null)}
          onConfirm={handleDenyConfirm}
        />
      )}
    </section>
  );
}
